import { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { Loading, Empty } from "../components/states";
import {
  Button,
  Money,
  ModelledFootnote,
  ModelledTag,
  Footnote,
  SectionHeading,
  SubHeading,
} from "../components/kit";
import { useBusyAction } from "../lib/useBusyAction";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * The savings-share rule engine. Owner only.
 *
 * This is demo checkbox 5: change a rule, watch a clinic's price move, and see
 * in plain words why. The live preview is the part that makes it persuasive —
 * a number that changes while Dee is watching beats any explanation of how the
 * cascade works.
 *
 * Which is why, at `lg`, the preview is a **sticky right-hand column beside the
 * rules** rather than a panel above them. Flipping a share used to move a
 * number that had scrolled off the top of the screen; now the rule being
 * changed and the price it changes are in the same glance, and the rule that is
 * currently pricing the previewed item is ringed so it is obvious which of the
 * eight to press.
 *
 * **The moving price is the loudest thing on this screen, and nothing else may
 * compete with it.** Two things used to. The modelled-cost caveat was a yellow
 * box at the top — it is now a `ModelledTag` on the margin figure itself plus
 * one quiet `Footnote`, which keeps the disclosure travelling with the number
 * it qualifies instead of out-shouting the demo. And the price panel itself was
 * a dark slate card with amber body copy inside it, i.e. the highest-contrast
 * object on the app's best screen was its chrome. It is a white card now; the
 * only thing in it drawn large is the price.
 */

/** The cascade's vocabulary, in the words the rule cards use. `describeRule` on
 *  the server says "Category: ENDO"; this is the same idea with the value
 *  stripped, because the ladder is about *shape*, not about any one rule. */
const SCOPE_WORD: Record<string, string> = {
  item: "Item",
  subcategory: "Subcategory",
  topCategory: "Category",
  global: "All items",
};

const SHARES = [0.4, 0.5, 0.6, 0.75];

/**
 * The live price check, before its three queries have landed.
 *
 * Same card, same left rule, same footprint — heading, item line, the big
 * figure, two rows of detail, the rule strip, the two pickers — so nothing on
 * the screen moves when the real price arrives. It is not `states.tsx`'s
 * `Loading`, which is a stack of generic rows: this panel's whole point is that
 * one number is enormous, and a skeleton that does not say so lets the layout
 * jump by 30px at exactly the moment the client is looking at it.
 *
 * `aria-busy` is for the reader's screen reader and for `tools/screens.mjs`
 * alike; `data-screen-state="loading"` is the marker the harness's settle
 * predicate looks for. Both, because both audiences are real.
 */
function PricePanelSkeleton() {
  return (
    <Card
      role="status"
      aria-busy="true"
      aria-live="polite"
      data-screen-state="loading"
      /* `block gap-0 p-4` is the shim every card on this screen applies: the
         registry card is built around header/content slots this panel does not
         use, and it lays itself out. */
      className="block gap-0 border-l-4 border-l-primary p-4"
    >
      <span className="sr-only">Working out the live clinic price…</span>
      <SubHeading>Live price check</SubHeading>
      <Skeleton className="mt-1 h-4 w-3/5" />
      <Skeleton className="mt-1.5 h-3 w-2/5" />
      {/* The figure's own height, not a generic bar: this is what stops the
          card growing when the price lands. */}
      <Skeleton className="mt-3 h-10 w-2/3 sm:h-12" />
      <Skeleton className="mt-2 h-3 w-1/2" />
      <div className="mt-3 space-y-2 border-t pt-3">
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-4/5" />
      </div>
      <Skeleton className="mt-3 h-8 w-full" />
      {/* Two `h-9` blocks: the stock select trigger's own height, so the two
          pickers below do not move the card when they arrive. */}
      <div className="mt-3 grid grid-cols-2 gap-2">
        <Skeleton className="h-9" />
        <Skeleton className="h-9" />
      </div>
    </Card>
  );
}

export function Pricing() {
  const data = useQuery(api.pricing.listRules, {});
  const costBasis = useQuery(api.pricing.costBasisSummary, {});
  const clinics = useQuery(api.clinics.list, {});
  const upsert = useMutation(api.pricing.upsertRule);
  const retire = useMutation(api.pricing.retireRule);

  const [previewItem, setPreviewItem] = useState<Id<"items"> | null>(null);
  const [previewClinic, setPreviewClinic] = useState<Id<"clinics"> | null>(
    null,
  );
  const { busy, run } = useBusyAction();

  const clinic = previewClinic ?? clinics?.[0]?._id ?? null;

  // The palette is clinic-scoped by design: it answers "what does THIS clinic
  // order". An owner has no clinic of their own, so without passing one
  // explicitly it comes back empty — and the live price check, which is the
  // whole point of this screen, silently disappears.
  const palette = useQuery(
    api.catalogue.palette,
    clinic ? { clinicId: clinic, limit: 12 } : "skip",
  );
  const item = previewItem ?? palette?.[0]?._id ?? null;

  const preview = useQuery(
    api.pricing.preview,
    item && clinic ? { itemId: item, clinicId: clinic } : "skip",
  );

  if (data === undefined || costBasis === undefined) {
    return <Loading rows={5} label="Loading pricing rules" />;
  }

  const changeShare = (ruleId: Id<"pricingRules">, share: number) => {
    const rule = data.rules.find((r) => r._id === ruleId);
    if (!rule) return;
    void run(
      () =>
        upsert({
          ruleId,
          scope: rule.scope as any,
          scopeValue: rule.scopeValue,
          clinicId: rule.clinicId,
          savingsShareToClinic: share,
        }),
      {
        failed: "Could not update the rule",
        done: {
          happened: `Clinic now keeps ${Math.round(share * 100)}% of the saving`,
          next: "Every invoice raised from now on uses the new split.",
        },
      },
    );
  };

  return (
    <div className="space-y-5 pb-8">
      <SectionHeading
        title="Pricing rules"
        subtitle="What share of each saving the clinic keeps. Most specific rule wins."
      />

      <ModelledFootnote
        modelled={costBasis.modelled}
        total={costBasis.total}
        bandMin={costBasis.bandMin}
        bandMax={costBasis.bandMax}
      />

      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-start lg:gap-6">
        {/* The live preview. Checkbox 5 happens here. First in the DOM because
            on a phone it belongs above the rules; placed right at `lg`, where
            it sticks beside them.

            The column is **always rendered**, skeleton or price. It used to be
            `{preview ? … : null}`, and the price depends on three chained
            queries — clinics, then that clinic's palette, then the preview — so
            on the app's best screen the right-hand column was simply blank for
            as long as those took, on a projector, with nothing saying anything
            was coming.

            That absence also defeated `tools/screens.mjs`: its settle predicate
            waits on `aria-busy` / `data-screen-state="loading"` / a loading
            report, and a panel that renders nothing sets none of them, so the
            capture was told the screen was ready and photographed the gap. The
            skeleton below carries `aria-busy` and `data-screen-state="loading"`,
            which is the same fact stated to the reader and to the harness at
            once. */}
        <div className="lg:sticky lg:top-20 lg:col-start-2 lg:row-start-1">
          {preview ? (
            <Card className="block gap-0 border-l-4 border-l-primary p-4">
              <SubHeading>Live price check</SubHeading>
              <div className="mt-1 truncate text-sm font-medium">
                {preview.itemName}
              </div>
              <div className="text-xs text-muted-foreground">
                at {preview.clinicName}
              </div>

              {/* The one large figure on the screen. Everything beside it is
                  secondary by size, not by colour. */}
              <div className="mt-3">
                <div className="num text-4xl font-semibold tracking-tight sm:text-5xl">
                  <Money cents={preview.clinicUnitPriceCents} />
                </div>
                <div className="mt-1 text-sm font-medium text-muted-foreground">
                  the clinic pays, per unit
                </div>
              </div>

              <dl className="mt-3 space-y-1 border-t pt-2 text-sm">
                {preview.clinicSavesCents > 0 ? (
                  <div className="flex items-baseline justify-between gap-3">
                    <dt className="text-muted-foreground">Clinic saves vs list</dt>
                    <dd className="font-semibold">
                      <Money cents={preview.clinicSavesCents} />
                    </dd>
                  </div>
                ) : null}
                <div className="flex items-baseline justify-between gap-3">
                  <dt className="flex items-center gap-1.5 text-muted-foreground">
                    Nexora margin
                    {preview.costIsModelled ? <ModelledTag /> : null}
                  </dt>
                  <dd className="font-semibold">
                    <Money cents={preview.marginCentsPerUnit} />
                  </dd>
                </div>
              </dl>

              {/* Why this price is this price, in words, beside the price.
                  Muted rather than accent-tinted: teal is interaction in this
                  app, and an explanation is not a control. Same strip as the
                  one on an invoice line. */}
              <p className="mt-3 rounded-md border-l-2 bg-muted px-2.5 py-1.5 text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">Priced by</span>{" "}
                {preview.appliedRuleLabel}
              </p>

              {/* The two pickers, on the registry's `Select` — the same move
                  the kit's `PeriodPicker` made, and for the same reason: two
                  unstyled platform dropdowns sitting beside shadcn triggers is
                  the seam a client notices.

                  `aria-label` moves onto the trigger, which is the element
                  Radix gives the combobox role to, so the accessible names are
                  the strings they always were. `w-full` is the one class: the
                  stock trigger is `w-fit`, and these two share a two-column
                  grid where a clinic named "Dee's" and one named
                  "Coquitlam Family Dental" must not be different widths.

                  `undefined` rather than `""` when nothing has resolved yet —
                  Radix treats the empty string as a reserved value. */}
              <div className="mt-3 grid grid-cols-2 gap-2">
                <Select
                  value={item ?? undefined}
                  onValueChange={(value) => setPreviewItem(value as Id<"items">)}
                >
                  <SelectTrigger aria-label="Item to preview" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(palette ?? []).map((option) => (
                      <SelectItem key={option._id} value={option._id}>
                        {option.name.slice(0, 32)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={clinic ?? undefined}
                  onValueChange={(value) =>
                    setPreviewClinic(value as Id<"clinics">)
                  }
                >
                  <SelectTrigger aria-label="Clinic to preview" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(clinics ?? []).map((option) => (
                      <SelectItem key={option._id} value={option._id}>
                        {option.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* The caveat, under the figure it qualifies, at the size a
                  caveat deserves. Non-dismissible; nothing hides it. */}
              {preview.costIsModelled ? (
                <Footnote tone="caution" className="mt-3">
                  {preview.modelledWarning}
                </Footnote>
              ) : null}
            </Card>
          ) : (
            <PricePanelSkeleton />
          )}
        </div>

        <div className="mt-5 space-y-3 lg:col-start-1 lg:row-start-1 lg:mt-0">
          <div>
            <SubHeading>Rules, most specific first</SubHeading>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              A rule higher in this list beats every rule below it. Scope beats
              clinic: an all-clinic rule on one item outranks a clinic-specific
              rule on that item's whole category.
            </p>

            {/* The cascade itself, drawn from the server's own order rather
                than from this file's memory of it. Eight rungs, in the order
                they are tried — so "most specific first" is something Gloria
                can see instead of something she has to take on trust. */}
            <ol className="mt-2 flex flex-wrap items-center gap-1">
              {data.cascade.map((tier, index) => (
                <li
                  key={`${tier.scope}-${tier.clinicSpecific}`}
                  className="flex items-center gap-1"
                >
                  {index > 0 ? (
                    <span aria-hidden className="text-muted-foreground">
                      ›
                    </span>
                  ) : null}
                  {/* Stock `outline`: a rung of the cascade is neither a
                      control nor a status, and `default` is `--primary`, which
                      in this app means "press me". `gap-0` is the one class —
                      the base's `gap-1` would put 4px between the scope word
                      and the " · all" that reads as part of the same phrase. */}
                  <Badge variant="outline" className="gap-0">
                    {SCOPE_WORD[tier.scope] ?? tier.scope}
                    <span className="text-muted-foreground">
                      {tier.clinicSpecific ? " · one clinic" : " · all"}
                    </span>
                  </Badge>
                </li>
              ))}
            </ol>
          </div>

          {data.rules.length === 0 ? (
            <Empty title="No rules yet" />
          ) : (
            data.rules.map((rule, index) => {
              const applied = preview?.appliedRuleId === rule._id;
              return (
                <Card
                  key={rule._id}
                  className={cn(
                    "block gap-0 p-4",
                    !rule.active
                      ? "bg-muted opacity-60"
                      : applied
                        ? "border-primary"
                        : "",
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        {/* Position in the cascade, not the raw tier index —
                            "4" told the reader nothing; "1st of 8" is the whole
                            point of the list being ordered. Square rather than
                            the badge's pill, because it holds one digit. */}
                        <Badge
                          variant="secondary"
                          className="num size-5 p-0 font-semibold"
                        >
                          {index + 1}
                        </Badge>
                        <span className="truncate font-medium">
                          {rule.scopeLabel}
                        </span>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                        <Badge variant="secondary">
                          {SCOPE_WORD[rule.scope] ?? rule.scope}
                        </Badge>
                        <span>{rule.clinicName ?? "All clinics"}</span>
                        {rule.active ? null : <span>· retired</span>}
                        {/* Accent, and the one place on this screen it is not a
                            control: it marks the rule the live preview above is
                            currently resolving to, which is a selection. Never
                            red, never green — a pricing rule has no status.
                            Tinted rather than solid so it does not read as the
                            fifth button in a row of share toggles. */}
                        {applied ? (
                          <Badge
                            variant="secondary"
                            className="bg-primary/10 text-primary"
                          >
                            Pricing the preview
                          </Badge>
                        ) : null}
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="num text-2xl font-semibold tracking-tight">
                        {Math.round(rule.savingsShareToClinic * 100)}%
                      </div>
                      <div className="text-xs text-muted-foreground">
                        to clinic
                      </div>
                    </div>
                  </div>

                  {rule.active ? (
                    <div className="mt-3 flex flex-wrap items-center gap-1.5">
                      {/* The segmented toggle, on the registry's own two
                          variants. `aria-pressed` stays — it is what tells a
                          screen reader which of the four shares is live, and
                          the kit's `Button` spreads it through now. `min-w-14`
                          is the one class: "40%" and "100%" are different
                          widths and four buttons that shift as you press them
                          read as four different controls. */}
                      {SHARES.map((share) => {
                        const selected =
                          Math.abs(rule.savingsShareToClinic - share) < 0.001;
                        return (
                          <Button
                            key={share}
                            variant={selected ? "default" : "outline"}
                            disabled={busy}
                            aria-pressed={selected}
                            onClick={() => changeShare(rule._id, share)}
                            className="min-w-14"
                          >
                            {Math.round(share * 100)}%
                          </Button>
                        );
                      })}
                      <Button
                        variant="ghost"
                        disabled={busy}
                        className="ml-auto"
                        onClick={() => {
                          void run(() => retire({ ruleId: rule._id }), {
                            failed: "Could not retire the rule",
                            done: {
                              happened: "Rule retired",
                              next: "Pricing now falls through to the next rule down the list.",
                            },
                          });
                        }}
                      >
                        Retire
                      </Button>
                    </div>
                  ) : null}
                </Card>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
